/**
 * Shared formatting utilities used across multiple components.
 */

/**
 * Formats a byte count into a human-readable string (e.g. "1.23 MB").
 */
export function formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Formats a number as a USD currency string using `Intl.NumberFormat`.
 *
 * The currency is hardcoded to USD for now but can be made configurable
 * later (e.g. from user preferences or locale detection) without changing
 * every call-site.
 *
 * @param amount  The numeric dollar amount.
 * @param options Optional overrides forwarded to `Intl.NumberFormat`.
 *                Pass `{ minimumFractionDigits: 3 }` for token-pricing
 *                displays that need sub-cent precision.
 */
export function formatCurrency(
    amount: number,
    options?: Intl.NumberFormatOptions,
): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...options,
    }).format(amount);
}

/**
 * Formats a dollar amount consistently across the app.
 *
 * - Values >= $1:    "$X.XX" / "$X,XXX.XX" (locale grouping for >= $100)
 * - Values >= $0.01: "$0.0042"   (4 decimals)
 * - Values < $0.01:  "$0.000123" (6 decimals)
 *
 * @param amount - Dollar value (NOT cents).
 */
export function formatUSD(amount: number): string {
    const safe = Number(amount) || 0;
    const abs = Math.abs(safe);

    if (abs >= 1) {
        return '$' + safe.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }
    if (abs >= 0.01) {
        return '$' + safe.toFixed(4);
    }
    return '$' + safe.toFixed(6);
}

/**
 * Formats a dollar amount with high precision (fuel credits, micro-costs).
 * Always shows 4 decimal places; uses locale grouping for values >= $100.
 */
export function formatFuel(amount: number): string {
    const safe = Number(amount) || 0;
    const abs = Math.abs(safe);

    if (abs >= 100) {
        return '$' + safe.toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
        });
    }
    return '$' + safe.toFixed(4);
}

/**
 * Formats token pricing per-1M values (3 decimal places).
 */
export function formatPricePer1M(amount: number): string {
    const safe = Number(amount) || 0;
    return '$' + safe.toFixed(3);
}

/**
 * Normalizes a raw backend tier string to a canonical Title-Case label for
 * display in the UI.  Both AccountSettings and BillingSettings use this so
 * the two components always show the same label for the same tier.
 *
 * Backend values: 'free' | 'monthly' | 'yearly' | 'lifetime' | 'guest' |
 * 'infra' | 'infra_credits' | ''
 */
export function formatTierLabel(tier?: string | null): string {
    switch ((tier || '').toLowerCase().trim()) {
        case 'superadmin':    return 'Superadmin';
        case 'admin':         return 'Admin';
        case 'lifetime':      return 'Founder';
        case 'monthly':       return 'Pro (Monthly)';
        case 'yearly':        return 'Pro (Yearly)';
        case 'pro':           return 'Pro';
        case 'guest':         return 'Guest';
        case 'infra':         return 'Infra';
        case 'infra_credits': return 'Infra + Credits';
        case 'free':
        default:              return 'Free';
    }
}
